import type { TFunction } from 'i18next'

import { ApiError, NEPOZNATA_GRESKA } from '../api/problem'
import { staffSesija } from '../auth/session'
import { jeGostovaAdresa } from '../app/rute'

/**
 * Prijevod grešaka. Poslužitelj u `title` šalje i18n KLJUČ, ne rečenicu
 * (CLAUDE.md §9), pa je ovdje jedino mjesto na kojem ključ postaje tekst.
 *
 * Nepoznat ključ pada na općenitu poruku, a ne na sam ključ: korisnik ne treba
 * vidjeti `pricing_rule_scope_invalid` ako netko doda novu grešku prije nego što
 * dođe prijevod.
 */

export function porukaGreske(t: TFunction, greska: unknown): string {
  const sesija = sesijskaGreska(greska)

  if (sesija) {
    return sesija.uloga
      ? t('errors.sesija_tuda', { uloga: t(`uloge.${sesija.uloga}`, { defaultValue: sesija.uloga }) })
      : t(`errors.${sesija.kljuc}`)
  }

  return t(`errors.${kljucGreske(greska)}`, { defaultValue: t(`errors.${NEPOZNATA_GRESKA}`) })
}

/**
 * Ključ iz onoga što je bačeno.
 *
 * **Nije svaka greška `ApiError`** (dotjerivanje-12 §1): priprema slike pada
 * PRIJE mreže — krivi format, HEIC s iPhonea, prevelika datoteka — i baca goli
 * `Error` čija je poruka i18n KLJUČ, a ne rečenica (`lib/varijante.ts`, isto
 * pravilo kao poslužiteljev `title`). Dok se to ovdje nije čitalo, gost je za
 * fotografiju s iPhonea dobivao "nepoznata greška" umjesto upute u kojem formatu
 * da je pošalje.
 *
 * Uzima se samo ono što IZGLEDA kao ključ (snake_case): poruka iznimke iz
 * preglednika ("Failed to fetch") ne smije završiti na ekranu kao tekst.
 */
function kljucGreske(greska: unknown): string {
  if (greska instanceof ApiError) return greska.key

  if (greska instanceof Error && /^[a-z][a-z0-9_]*$/.test(greska.message)) {
    return greska.message
  }

  return NEPOZNATA_GRESKA
}

/**
 * "NEPOZNATA GREŠKA" JE ZADNJE, NE PRVO (Z22c §E, nalaz 09.08.).
 *
 * Gost koji otvori aplikaciju u pregledniku u kojem je prijavljen vozač dobiva
 * 403 bez ijednog ključa — a to je do sada bila "nepoznata greška". Nije
 * nepoznata: površina traži GOSTOVU sesiju, a u pregledniku stoji tuđa. Isto
 * vrijedi za 401 bez sesije ("prijavite se") i za modul koji resort nema ("ova
 * usluga nije dostupna").
 *
 * **Račun stoji ovdje, na jednom mjestu**, jer ga pita i poruka i gumb koji uz
 * nju stoji (`izlazGreske`). Dva računa značila bi rečenicu "prijavite se" bez
 * gumba, ili gumb bez rečenice.
 *
 * **Uloga se čita SAMO na gostovoj površini**: u konzoli je 403 odluka o
 * ovlasti, a ne kriva sesija — ondje bi ista rečenica bila neistinita.
 */
function sesijskaGreska(greska: unknown): { kljuc: string; uloga: string | null } | null {
  if (!(greska instanceof ApiError)) return null

  // Modul koji resort nema nije greška nego stanje — i ta rečenica vrijedi na
  // svakoj površini, pa se ne veže uz sesiju.
  if (greska.key === 'modul_not_enabled') return { kljuc: 'modul_not_enabled', uloga: null }

  if (greska.status !== 401 && greska.status !== 403) return null

  // Poslužitelj koji je REKAO što je palo ima prednost: `access_blocked` i
  // `password_change_required` su odgovori, a ne izostanak sesije.
  if (greska.key !== NEPOZNATA_GRESKA) return null

  if (!naGostovoj()) return null

  if (greska.status === 401) return { kljuc: 'sesija_nema', uloga: null }

  const uloga = staffSesija.getState().uloga

  return uloga && uloga !== 'gost'
    ? { kljuc: 'sesija_tuda', uloga }
    : { kljuc: 'sesija_nema', uloga: null }
}

/**
 * Izlaz iz greške, ili `null` kad ga nema. Poruka bez izlaza je zid; gumb
 * "Prijavite se" pod njom je izlaz koji gost razumije bez objašnjenja.
 *
 * Modul koji resort nema izlaza NEMA i ne smije ga imati: ondje nema što
 * pritisnuti — usluga jednostavno ne postoji.
 */
export function izlazGreske(t: TFunction, greska: unknown): { tekst: string; na: string } | null {
  const sesija = sesijskaGreska(greska)

  if (!sesija?.kljuc.startsWith('sesija')) return null

  return { tekst: t('errors.sesija_gumb'), na: '/g/prijava' }
}

/** Je li aplikacija otvorena na gostovoj površini (`/g…`). */
function naGostovoj(): boolean {
  return jeGostovaAdresa(globalThis.location?.pathname ?? '')
}

/** Poruka uz konkretno polje obrasca (422 s popisom pravila koja su pala). */
export function porukaPolja(t: TFunction, greska: unknown, polje: string): string | undefined {
  if (!(greska instanceof ApiError)) return undefined

  const kod = greska.field(polje)
  if (!kod) return undefined

  return t(`validation.${kod}`, { defaultValue: t('validation.unknown') })
}

/** Jedan redak sažetka: koje polje i zašto. */
export interface GreskaPolja {
  polje: string
  /** Ljudski naziv polja; izostaje ako prijevoda nema (poruka i sama stoji). */
  oznaka?: string
  poruka: string
}

/** Je li greška validacijska (422 s popisom polja), a ne sukob ili kvar. */
export function jeValidacijska(greska: unknown): greska is ApiError {
  return greska instanceof ApiError && greska.status === 422 && Object.keys(greska.fields).length > 0
}

/**
 * 422 razložen po poljima — **koje polje i zašto**, ljudskim jezikom (Z13.1,
 * nalaz 2).
 *
 * Dotad je pult na svaki odbijeni obrazac dobivao jednu rečenicu
 * ("Provjerite označena polja"), koja mu ne kaže ni koje polje ni što s njim
 * nije u redu — a kad polje koje je palo obrazac uopće ne prikazuje (npr.
 * `soba` koja stiže iz rute, ili `krakovi` kao skup kvačica), poruka nije bila
 * samo nejasna nego neistinita: nije bilo ničega označenog.
 *
 * `prikazana` su polja koja obrazac već ispisuje sam, uz sebe — njih sažetak
 * preskače, jer je poruka uz polje bolja od iste poruke u podnožju.
 */
export function sazetakPolja(
  t: TFunction,
  greska: unknown,
  prikazana: readonly string[] = [],
): GreskaPolja[] {
  if (!jeValidacijska(greska)) return []

  return Object.entries(greska.fields)
    .filter(([polje]) => !prikazana.includes(polje))
    .map(([polje, kodovi]) => ({
      polje,
      oznaka: t(`validation.polje.${polje}`, { defaultValue: '' }) || undefined,
      poruka: t(`validation.${kodovi[0]}`, { defaultValue: t('validation.unknown') }),
    }))
}
